import { useState, type MouseEvent } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Menu,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '@/components/feedback/EmptyState';
import { formatRelative } from '@/utils/format';
import { paths } from '@/routes/paths';
import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useUnreadCount,
} from '@/features/notifications/hooks';
import type { NotificationLevel } from '@/features/notifications/types';

const LEVEL_COLOR: Record<NotificationLevel, string> = {
  info: 'info.main',
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
};

/** Top-bar notification centre: unread badge, recent list, live via Socket.IO. */
export function NotificationsMenu() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const unread = useUnreadCount();
  const recent = useNotifications({ limit: 8 });
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const count = unread.data?.count ?? 0;
  const items = recent.data?.items ?? [];

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={(e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
          aria-label={`Notifications${count ? `, ${count} unread` : ''}`}
        >
          <Badge badgeContent={count} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 380, maxWidth: '95vw' } } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1 }}
        >
          <Typography variant="subtitle2">Notifications</Typography>
          {count > 0 && (
            <Button
              size="small"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              Mark all read
            </Button>
          )}
        </Stack>
        <Divider />
        {items.length === 0 ? (
          <EmptyState title="You're all caught up" description="New notifications appear here." />
        ) : (
          <List dense disablePadding sx={{ maxHeight: 380, overflowY: 'auto' }}>
            {items.map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => !item.readAt && markRead.mutate(item.id)}
                sx={{ alignItems: 'flex-start', gap: 1.25, py: 1 }}
              >
                <Box
                  sx={{
                    mt: 0.75,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    flexShrink: 0,
                    bgcolor: item.readAt ? 'transparent' : LEVEL_COLOR[item.level],
                    border: (t) => (item.readAt ? `1px solid ${t.palette.divider}` : 'none'),
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={item.readAt ? 400 : 600} noWrap>
                    {item.title}
                  </Typography>
                  {item.body && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {item.body}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.disabled">
                    {formatRelative(item.createdAt)}
                  </Typography>
                </Box>
              </ListItemButton>
            ))}
          </List>
        )}
        <Divider />
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            onClick={() => {
              setAnchor(null);
              navigate(paths.notifications);
            }}
          >
            View all
          </Button>
        </Box>
      </Menu>
    </>
  );
}
